import { isInquiryPaymentType } from "@/helpers/isInquiryPaymentType.ts";
import { useCalculateCart } from "@/helpers/useCalculateCart.tsx";
import { getAPIUrl } from "@/stores/ApolloClient/helpers/getAPIUrl.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

// cancelCartCalculate is used to prevent the cart recalculation because this hook is used in two places:
// 1. ReservationSidebar - Full size component
// 2. MobileCartBar - Mobile component
// The recalculation should only happen in the component that is currently in use
export const useReservationSidebarData = (cancelCartCalculate?: boolean) => {
  const { debouncedCalculateCart, calculateCartLoading } = useCalculateCart(cancelCartCalculate);
  const {
    page,
    defaultDateRange,
    setDefaultDateRange,
    cartAccommodationItems,
    calculatedCartData,
    acceptedGDPR,
    setAcceptedGDPR,
    isGDPRHighlighted,
    setIsGDPRHighlighted,
    rootAttributes,
  } = useEngineStore(
    useShallow((state) => ({
      page: state.page,
      defaultDateRange: state.defaultDateRange,
      setDefaultDateRange: state.setDefaultDateRange,
      cartAccommodationItems: state.cartAccommodationItems,
      calculatedCartData: state.calculatedCartData,
      acceptedGDPR: state.acceptedGDPR,
      setAcceptedGDPR: state.setAcceptedGDPR,
      isGDPRHighlighted: state.isGDPRHighlighted,
      setIsGDPRHighlighted: state.setIsGDPRHighlighted,
      rootAttributes: state.rootAttributes,
    })),
  );

  const isForcedInquiry = isInquiryPaymentType(calculatedCartData);

  useEffect(() => {
    debouncedCalculateCart();

    return () => {
      debouncedCalculateCart.cancel();
    };
  }, [debouncedCalculateCart]);

  const isCartItemInDisabledRateGroupFromRootAttributes = useMemo(() => {
    if (getAPIUrl().includes("simuni")) {
      return cartAccommodationItems.some(
        (item) =>
          [
            // centar
            "327a7f7d-274d-4adb-b632-8ec3030448ae",
            // jug
            "a7468911-bb30-4ec0-ae73-f7bc0a364d36",
            // punta
            "beea4399-8223-43da-848f-73fd64b24294",
          ]?.includes(item.rateGroup?.id),
      );
    } else {
      if (rootAttributes?.[RootAttribute.DisableRateGroupContinue]?.length) {
        return cartAccommodationItems.some(
          (item) => rootAttributes?.[RootAttribute.DisableRateGroupContinue]?.includes(item.rateGroup?.id),
        );
      }
    }

    return false;
  }, [rootAttributes, cartAccommodationItems]);

  return {
    page,
    defaultDateRange,
    setDefaultDateRange,
    cartAccommodationItems,
    calculateCartLoading,
    calculatedCartData,
    acceptedGDPR,
    setAcceptedGDPR,
    isGDPRHighlighted,
    setIsGDPRHighlighted,
    isCartItemInDisabledRateGroupFromRootAttributes,
    isForcedInquiry,
  };
};
